import type { CalendarEvent } from "@/types/calendarEvent";

const API_KEY = import.meta.env.VITE_GOOGLE_CALENDAR_API_KEY;
const CALENDAR_ID = import.meta.env.VITE_GOOGLE_CALENDAR_ID;

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
let cachedEvent: CalendarEvent | null = null;
let cacheTimestamp: number | null = null;

export async function fetchNextEvent(): Promise<CalendarEvent | null> {
	const now = Date.now();

	if (cachedEvent && cacheTimestamp && now - cacheTimestamp < CACHE_DURATION) {
		return cachedEvent;
	}

	if (!API_KEY || !CALENDAR_ID) {
		console.error("Google Calendar API key or Calendar ID is not set");
		return null;
	}

	const nowISO = new Date().toISOString();
	const url = `https://www.googleapis.com/calendar/v3/calendars/${CALENDAR_ID}/events?key=${API_KEY}&timeMin=${nowISO}&singleEvents=true&orderBy=startTime&maxResults=1`;

	try {
		const response = await fetch(url);
		const data = await response.json();
		if (data.items && data.items.length > 0) {
			cachedEvent = data.items[0] as CalendarEvent;
			cacheTimestamp = now;
			return cachedEvent;
		}
		return null;
	} catch (error) {
		console.error("Error fetching next event:", error);
		return null;
	}
}

export function getLocationName(location: string): string {
	return location.split(",")[0];
}

export function getLocationImage(location: string): string {
	if (location.includes("大船渡テレワークセンター")) {
		return "/images/ofunatotelework.jpeg";
	}
	if (location.includes("おおふなぽーと")) {
		return "/images/ofunaport.jpg";
	}
	return "/images/default.jpg";
}

export function formatTimeRange(start: string, end: string): string {
	const startTime = new Date(start).toLocaleTimeString("ja-JP", {
		hour: "2-digit",
		minute: "2-digit",
	});
	const endTime = new Date(end).toLocaleTimeString("ja-JP", {
		hour: "2-digit",
		minute: "2-digit",
	});
	return `${startTime}~${endTime}`;
}

export function formatDateAndDay(dateString: string): {
	date: string;
	day: string;
} {
	const date = new Date(dateString);
	const formattedDate = date.toLocaleDateString("ja-JP", {
		month: "numeric",
		day: "numeric",
	});
	const formattedDay = date
		.toLocaleDateString("ja-JP", { weekday: "short" })
		.replace("曜日", "");
	return { date: formattedDate, day: formattedDay };
}

export function getYear(dateString: string): string {
	return new Date(dateString).getFullYear().toString();
}

export async function fetchUpcomingEvents(
	maxResults = 10,
): Promise<CalendarEvent[] | null> {
	if (!API_KEY || !CALENDAR_ID) {
		console.error("Google Calendar API key or Calendar ID is not set");
		return null;
	}

	const nowISO = new Date().toISOString();
	const url = `https://www.googleapis.com/calendar/v3/calendars/${CALENDAR_ID}/events?key=${API_KEY}&timeMin=${nowISO}&singleEvents=true&orderBy=startTime&maxResults=${maxResults}`;

	try {
		const response = await fetch(url);
		const data = await response.json();
		if (data.items && data.items.length > 0) {
			return data.items as CalendarEvent[];
		}
		return null;
	} catch (error) {
		console.error("Error fetching upcoming events:", error);
		return null;
	}
}
